import {
    constructTable, tableFeatures, rowSortingFeature, columnFilteringFeature,
    createSortedRowModel, createFilteredRowModel, type ColumnDef,
} from '@tanstack/table-core';
import { storeReactivityBindings } from '@tanstack/table-core/store-reactivity-bindings';

const features = tableFeatures({
    coreReactivityFeature: storeReactivityBindings(),
    rowSortingFeature, columnFilteringFeature,
    sortedRowModel: createSortedRowModel(),
    filteredRowModel: createFilteredRowModel(),
});
const initialized = new WeakSet<HTMLTableElement>();
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
type TableRecord = { element: HTMLTableRowElement; values: string[] };

function comparable(value: string): number | string {
    const text = value.trim();
    if (/^\(?-?\$?[\d,]+(?:\.\d+)?%?\)?$/.test(text)) return Number(text.replace(/[$,%()]/g, '')) * (text.startsWith('(') ? -1 : 1);
    const date = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s|$)/);
    if (date) return Date.UTC(Number(date[3]), Number(date[1]) - 1, Number(date[2]));
    if (/^\d{4}-\d{2}-\d{2}(?:\s|T|$)/.test(text)) {
        const parsed = Date.parse(text);
        if (!Number.isNaN(parsed)) return parsed;
    }
    const longDate = text.match(/^(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}/i);
    if (longDate) return Date.parse(longDate[0]);
    return text;
}

/** Preserve the common Tablesorter filter expressions used on the existing reports. */
function matchesFilter(value: string, query: string): boolean {
    const text = value.toLocaleLowerCase();
    const filter = query.trim().toLocaleLowerCase();
    if (!filter) return true;
    if (filter.includes('|')) return filter.split('|').some(part => matchesFilter(value, part));
    const compare = filter.match(/^(>=|<=|>|<)(.*)$/);
    if (compare) {
        const left = comparable(value), right = comparable(compare[2]!);
        if (typeof left !== 'number' || typeof right !== 'number') return false;
        return compare[1] === '>=' ? left >= right : compare[1] === '<=' ? left <= right : compare[1] === '>' ? left > right : left < right;
    }
    if (filter.startsWith('!')) return !text.includes(filter.slice(1));
    if (filter.startsWith('=')) return text === filter.slice(1).trim();
    if (filter.startsWith('^')) return text.startsWith(filter.slice(1));
    if (filter.endsWith('$')) return text.endsWith(filter.slice(0, -1));
    return text.includes(filter);
}

/** Generated picker menus are controls, not searchable table data. */
function cellValue(cell: HTMLTableCellElement): string {
    if (cell.dataset.sortValue !== undefined) return cell.dataset.sortValue.trim();
    if (cell.dataset.text !== undefined) return cell.dataset.text.trim();
    const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT, {
        acceptNode: node => node.parentElement?.closest('[data-native-picker]') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
    });
    let text = '';
    while (walker.nextNode()) text += walker.currentNode.textContent || '';
    return text.trim();
}

/** Keep server-rendered row nodes intact so form values, Alpine state and handlers survive sorting. */
function enhanceTable(el: HTMLTableElement) {
    const body = el.tBodies[0];
    const head = el.tHead;
    if (!body || !head || !head.rows[0] || initialized.has(el)) return;
    initialized.add(el);
    el.dataset.tableEngine = 'tanstack';
    el.classList.add('dui-table', 'ab-data-table');
    const headers = [...head.rows[0].cells];
    const records = () => [...body.rows].map(element => ({
        element,
        values: [...element.cells].map(cellValue),
    }));
    const table = constructTable<typeof features, TableRecord>({
        features,
        data: records(),
        columns: headers.map((header, index): ColumnDef<typeof features, TableRecord> => ({
            id: String(index),
            accessorFn: (row: TableRecord) => row.values[index] || '',
            enableSorting: header.dataset.sorter !== 'false' && !header.classList.contains('sorter-false'),
            sortDescFirst: false,
            sortFn: (a, b) => {
                const key = header.dataset.sortKey;
                const left = comparable((key && a.original.element.getAttribute(`data-sort-${key}`)) || a.original.values[index] || '');
                const right = comparable((key && b.original.element.getAttribute(`data-sort-${key}`)) || b.original.values[index] || '');
                const comparison = typeof left === 'number' && typeof right === 'number' ? left - right : collator.compare(String(left), String(right));
                return comparison || (key ? collator.compare(a.original.values[index] || '', b.original.values[index] || '') : 0);
            },
            filterFn: (row, _columnId, query: unknown) => matchesFilter(row.original.values[index] || '', String(query ?? '')),
        })),
        enableSortingRemoval: false,
    });
    // Some tables already have an Alpine search/filter UI for their records.
    const filterRow = el.dataset.tableFilters === 'false' ? null : head.insertRow();
    if (filterRow) filterRow.className = 'ab-table-filters';
    headers.forEach((header, i) => {
        if (filterRow) {
            const cell = filterRow.insertCell();
            const input = document.createElement('input');
            input.type = 'search';
            input.className = 'dui-input ab-table-filter';
            input.setAttribute('aria-label', `Filter ${header.textContent?.trim() || `column ${i + 1}`}`);
            input.disabled = header.dataset.filter === 'false' || header.classList.contains('filter-false');
            input.addEventListener('input', () => {
                table.getColumn(String(i))!.setFilterValue(input.value);
                render();
            });
            cell.append(input);
        }
        if (!table.getColumn(String(i))!.getCanSort()) return;
        header.tabIndex = 0;
        header.setAttribute('aria-sort', 'none');
        header.classList.add('ab-sortable');
        const sort = (event: MouseEvent | KeyboardEvent) => {
            if (event instanceof KeyboardEvent && !['Enter', ' '].includes(event.key)) return;
            const control = event.target instanceof Element ? event.target.closest<HTMLElement>('a[data-sort-key], button[data-sort-key]') : null;
            if (!control && event.target instanceof Element && event.target.closest('input, select, a, button')) return;
            event.preventDefault();
            const changedKey = control && control.dataset.sortKey !== header.dataset.sortKey;
            if (control) header.dataset.sortKey = control.dataset.sortKey;
            // Switching Name/Surname must invalidate the previous sorted model even when both ascend.
            if (changedKey) table.setSorting([]);
            table.getColumn(String(i))!.toggleSorting(changedKey ? false : undefined, event.shiftKey);

            render();
        };
        header.addEventListener('click', sort);
        header.addEventListener('keydown', sort);
    });
    const status = document.createElement('span');
    status.className = 'sr-only';
    status.setAttribute('role', 'status');
    el.after(status);
    const observer = new MutationObserver(changes => {
        // Searching an embedded picker changes its popup, not the table's records.
        // Moving the row in response would dismiss its native popover.
        if (changes.every(change => (change.target instanceof Element ? change.target : change.target.parentElement)?.closest('[data-native-picker]'))) return;
        const data = records();
        const previous = new Map(table.options.data.map(row => [row.element, row.values]));
        if (data.length === previous.size && data.every(row => {
            const values = previous.get(row.element);
            return values?.length === row.values.length && row.values.every((value, index) => value === values[index]);
        })) return;
        table.setOptions(previous => ({ ...previous, data }));
        render();
    });
    function render() {
        observer.disconnect();
        const visible = table.getRowModel().rows;
        const included = new Set(visible.map(row => row.original.element));
        const focused = document.activeElement;
        // Both removal and insertion must be shielded from Alpine's observer.
        // Detaching rows first would destroy their x-for listeners and effects.
        window.Alpine.mutateDom(() => {
            const fragment = document.createDocumentFragment();
            visible.forEach((row, i) => {
                row.original.element.hidden = false;
                row.original.element.classList.toggle('odd', i % 2 === 0);
                row.original.element.classList.toggle('even', i % 2 !== 0);
                fragment.append(row.original.element);
            });
            table.options.data.forEach(row => {
                if (!included.has(row.element)) { row.element.hidden = true; fragment.append(row.element); }
            });
            body.append(fragment);
        });
        if (focused instanceof HTMLElement && body.contains(focused)) focused.focus({ preventScroll: true });
        headers.forEach((header, i) => {
            const sorted = table.getColumn(String(i))!.getIsSorted();
            header.querySelectorAll<HTMLElement>('a[data-sort-key], button[data-sort-key]').forEach(control => {
                control.classList.toggle('font-bold', !!sorted && control.dataset.sortKey === header.dataset.sortKey);
                control.setAttribute('aria-label', `Sort by ${control.textContent?.trim()}`);
            });
            if (header.classList.contains('ab-sortable')) header.setAttribute('aria-sort', sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none');
        });
        status.textContent = `${visible.length} of ${table.options.data.length} rows`;
        observer.observe(body, { childList: true, subtree: true, characterData: true });
    }
    if (el.dataset.initialSort !== undefined && table.getColumn(el.dataset.initialSort)?.getCanSort())
        table.setSorting([{ id: el.dataset.initialSort, desc: false }]);
    render();
}

/** Keep wide data tables inside their panel while preserving their existing row nodes. */
function makeTableScrollable(table: HTMLTableElement) {
    if (!table.tHead || table.closest('.cke, .datepicker') || table.parentElement?.closest('table, [data-table-scroll]')) return;
    const viewport = document.createElement('div');
    viewport.dataset.tableScroll = '';
    viewport.className = 'w-full min-w-0 max-w-full overflow-x-auto mb-4 print:overflow-visible';
    viewport.tabIndex = 0;
    viewport.setAttribute('role', 'region');
    viewport.setAttribute('aria-label', `${table.caption?.textContent?.trim() || 'Table'} scroll area`);
    window.Alpine.mutateDom(() => {
        table.before(viewport);
        viewport.append(table);
    });
}

export function enhanceTables(root: ParentNode) {
    const tables = [...(root instanceof HTMLTableElement ? [root] : []), ...root.querySelectorAll<HTMLTableElement>('table')];
    for (const table of tables) {
        makeTableScrollable(table);
        if (table.matches('.table-filter')) enhanceTable(table);
    }
}
